import React from 'react';
import { CheckCircle, XCircle, FileText, MessageSquare, Coins, Calendar, Users } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, subValue, variant = 'default' }) => {
  const variants = {
    default: 'dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200',
    success: 'dark:bg-green-500/10 bg-green-50 border dark:border-green-500/30 border-green-200',
    warning: 'dark:bg-amber-500/10 bg-amber-50 border dark:border-amber-500/30 border-amber-200',
    error: 'dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200',
  };

  const iconVariants = {
    default: 'text-sky-500',
    success: 'text-green-500',
    warning: 'text-amber-500',
    error: 'text-red-500',
  };

  return (
    <div className={`rounded-xl p-5 ${variants[variant]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium dark:text-slate-400 text-slate-500 mb-1">
            {label}
          </p>
          <p className="text-2xl font-bold dark:text-white text-slate-900">
            {value}
          </p>
          {subValue && (
            <p className="text-xs dark:text-slate-500 text-slate-400 mt-1">
              {subValue}
            </p>
          )}
        </div>
        <div className={`p-2 rounded-lg dark:bg-slate-800 bg-slate-100 ${iconVariants[variant]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const FBMonitorStats = ({ stats, sessionStatus, sourcesCount }) => {
  const getSessionStatusDisplay = () => {
    if (!sessionStatus) return { label: 'Unknown', variant: 'default', icon: XCircle };

    const status = sessionStatus.status?.toLowerCase();

    // Handle expired states
    if (status === 'expired') {
      return { label: 'Expired', variant: 'error', icon: XCircle };
    }

    // Handle no session / disconnected states
    if (status === 'no_session' || status === 'disconnected') {
      return { label: 'Disconnected', variant: 'error', icon: XCircle };
    }

    // Handle expiring soon
    if (status === 'expiring_soon' || status === 'expiring') {
      return {
        label: `Expiring in ${sessionStatus.days_remaining || '?'}d`,
        variant: 'warning',
        icon: CheckCircle
      };
    }

    // Any other status (active, valid, connected, etc.) means connected
    return { label: 'Connected', variant: 'success', icon: CheckCircle };
  };

  const sessionDisplay = getSessionStatusDisplay();

  const formatNumber = (num) => {
    if (!num && num !== 0) return '—';
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard
        icon={sessionDisplay.icon || CheckCircle}
        label="Session Status"
        value={sessionDisplay.label}
        variant={sessionDisplay.variant}
      />

      <StatCard
        icon={Users}
        label="Active Sources"
        value={formatNumber(sourcesCount)}
      />

      <StatCard
        icon={FileText}
        label="Total Reports"
        value={formatNumber(stats?.total_reports)}
      />

      <StatCard
        icon={MessageSquare}
        label="Posts Analyzed"
        value={formatNumber(stats?.total_posts_analyzed)}
      />

      <StatCard
        icon={Coins}
        label="Credits Used"
        value={formatNumber(stats?.total_credits_used)}
      />

      <StatCard
        icon={Calendar}
        label="Last Report"
        value={formatDate(stats?.last_report_date)}
      />
    </div>
  );
};

export default FBMonitorStats;
